import { useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslations } from "next-intl";
import { makeBackgroundInert, wrapTabFocus } from "@/lib/modal-focus";

/** What the API reported about students' data on the item (F1, 409 HAS_STUDENT_DATA / HAS_GRADED_WORK). */
export type StudentDataCounts = {
  progress: number;
  forumThreads: number;
  submissions: number;
  quizAttempts: number;
};

type Props = {
  /** The question, e.g. "Delete the session “Ascendant”?". */
  message: string;
  /** Set after a 409 HAS_STUDENT_DATA: confirming again also deletes this data. */
  studentData?: StudentDataCounts | null;
  /** Set after a 409 HAS_GRADED_WORK: nothing can be deleted, only closed. */
  gradedWork?: StudentDataCounts | null;
  /** R3-L12: set after the API said this delete leaves a published course without sessions. */
  emptiesPublishedCourse?: boolean;
  error?: string | null;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * F1: confirmation for deleting a module or session. A modal dialog whose message escalates
 * with what the server says. R3-L11: rendered at the top of the page with everything else inert,
 * Tab stays inside it, Escape cancels, and focus goes back to the opener when it closes (the
 * list moves focus on to its heading after a successful delete).
 */
export function ConfirmDeleteDialog({
  message,
  studentData = null,
  gradedWork = null,
  emptiesPublishedCourse = false,
  error = null,
  busy = false,
  onConfirm,
  onCancel,
}: Props) {
  const t = useTranslations("instructor");
  const titleId = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    const restoreBackground = rootRef.current ? makeBackgroundInert(rootRef.current) : () => {};
    cancelRef.current?.focus();
    return () => {
      restoreBackground();
      opener?.focus?.();
    };
  }, []);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.stopPropagation();
      onCancel();
      return;
    }
    wrapTabFocus(e, rootRef.current);
  }

  const askAgain = Boolean(studentData) || emptiesPublishedCourse;

  return createPortal(
    <div
      ref={rootRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-testid="confirm-delete-dialog"
        onKeyDown={handleKeyDown}
        className="w-full max-w-md rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-5 text-[var(--text-primary)] shadow-lg"
      >
        <p id={titleId} className="font-medium">
          {message}
        </p>
        {gradedWork ? (
          <p
            className="mt-3 text-sm text-[var(--danger)]"
            role="alert"
            data-testid="confirm-delete-blocked"
          >
            {t("deleteBlockedGradedWork", {
              submissions: gradedWork.submissions,
              quizAttempts: gradedWork.quizAttempts,
            })}
          </p>
        ) : studentData ? (
          <p
            className="mt-3 text-sm text-[var(--text-secondary)]"
            role="status"
            data-testid="confirm-delete-student-data"
          >
            {t("deleteStudentDataWarning", {
              progress: studentData.progress,
              forumThreads: studentData.forumThreads,
            })}
          </p>
        ) : null}
        {!gradedWork && emptiesPublishedCourse ? (
          <p
            className="mt-3 text-sm text-[var(--text-secondary)]"
            role="status"
            data-testid="confirm-delete-empties-course"
          >
            {t("deleteEmptiesPublishedCourseWarning")}
          </p>
        ) : null}
        {error ? (
          <p
            className="mt-3 text-sm text-[var(--danger)]"
            role="alert"
            data-testid="confirm-delete-error"
          >
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            data-testid="confirm-delete-cancel"
            onClick={onCancel}
            className="rounded border px-4 py-2 text-sm hover:bg-[var(--surface-3)]"
          >
            {gradedWork ? t("close") : t("cancel")}
          </button>
          {gradedWork ? null : (
            <button
              type="button"
              data-testid="confirm-delete-submit"
              aria-disabled={busy}
              onClick={() => {
                if (!busy) onConfirm();
              }}
              className="rounded bg-red-600 px-4 py-2 text-sm text-white aria-disabled:opacity-60"
            >
              {askAgain ? t("deleteAnyway") : t("deleteYes")}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
